import React, { useState, useEffect } from 'react';
import Spinner from '../components/Spinner';
import { toast } from "react-toastify";
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { v4 as uuidv4 } from "uuid";
import { addDoc, collection, serverTimestamp, getDoc, doc } from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaTrash } from "react-icons/fa";
import { MdCameraAlt } from "react-icons/md";
import { LuCircleFadingArrowUp } from "react-icons/lu";

export default function UploadStatus() {
  const navigate = useNavigate();
  const auth = getAuth();
  const [loading, setLoading] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [images, setImages] = useState([]); // Array to store image files
  const [imagePreviews, setImagePreviews] = useState([]); // Array to store image previews
  const [userData, setUserData] = useState(null);

  // Fetch user data to get notification subscribers
  useEffect(() => {
    async function fetchUserData() {
      if (!auth.currentUser) return;

      try {
        const userRef = doc(db, 'users', auth.currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          setUserData({
            name: data.name || 'Unknown',
            notificationSubscribers: Array.isArray(data.notificationSubscribers) ? data.notificationSubscribers : []
          });
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        toast.error('Failed to load user data');
      }
    }
    fetchUserData();
  }, [auth.currentUser]);

  // Helper function to create a notification
  const createNotification = async (type, recipientId, message, targetId = null) => {
    if (!auth.currentUser || auth.currentUser.uid === recipientId) return; // Don't notify self

    try {
      const notifRef = collection(db, `notifications/${recipientId}/userNotifications`);
      await addDoc(notifRef, {
        type,
        senderId: auth.currentUser.uid,
        targetId,
        message,
        timestamp: serverTimestamp(),
        isRead: false
      });
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  // Notify subscribers when a new status is posted
  const notifySubscribersOnStatusPost = async (statusId, statusContent) => {
    if (!userData || !userData.notificationSubscribers || userData.notificationSubscribers.length === 0) return;

    const message = `${userData.name} posted a new status: "${statusContent.substring(0, 50)}${statusContent.length > 50 ? '...' : ''}"`;
    
    const notificationPromises = userData.notificationSubscribers.map((subscriberId) =>
      createNotification('status', subscriberId, message, statusId)
    );

    await Promise.all(notificationPromises);
  };

  // Handle text input change
  function onTextChange(e) {
    setStatusText(e.target.value);
  }

  // Handle image upload
  function onImageChange(e) {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate image count
    if (images.length + files.length > 4) {
      toast.error("You can upload a maximum of 4 images.");
      return;
    }

    // Validate each file
    const newImages = [];
    const newPreviews = [];
    files.forEach(file => {
      if (!file.type.includes("image")) {
        toast.error("Please upload image files only.");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Each image must be less than 10MB.");
        return;
      }
      newImages.push(file);
      newPreviews.push(URL.createObjectURL(file));
    });

    setImages(prev => [...prev, ...newImages]);
    setImagePreviews(prev => [...prev, ...newPreviews]);
  }

  // Remove an image
  function removeImage(index) {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  }

  // Upload images to Firebase Storage
  async function storeImage(image) {
    return new Promise((resolve, reject) => {
      const storage = getStorage();
      const filename = `${auth.currentUser.uid}-${image.name}-${uuidv4()}`;
      const storageRef = ref(storage, `statusImages/${filename}`);
      const uploadTask = uploadBytesResumable(storageRef, image);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log('Upload is ' + progress + '% done');
        },
        (error) => {
          reject(error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            resolve(downloadURL);
          });
        }
      );
    });
  }

  // Handle form submission
  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);

    // Validate that at least text or one image is provided
    if (!statusText.trim() && images.length === 0) {
      setLoading(false);
      toast.error("Please provide a status text or upload at least one image.");
      return;
    }

    try {
      // Upload images if any
      const imageUrls = await Promise.all(
        images.map(image => storeImage(image))
      );

      // Prepare status data
      const statusData = {
        text: statusText.trim(),
        imageUrls: imageUrls.length > 0 ? imageUrls : [],
        timestamp: serverTimestamp(),
        userRef: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        likes: 0,
        savedBy: [],
        likedBy: []
      };

      // Add status to Firestore
      const docRef = await addDoc(collection(db, "status"), statusData);

      // Notify subscribers
      await notifySubscribersOnStatusPost(docRef.id, statusText.trim());

      setLoading(false);
      toast.success("Status posted successfully!");
      navigate(`/status/${docRef.id}`); // Redirect to Full Status Page
    } catch (error) {
      setLoading(false);
      toast.error("Error posting status. Please try again.");
      console.error("Upload error:", error);
    }
  }

  if (loading) {
    return <Spinner />;
  }

  return (
    <main className="min-h-screen bg-black text-white py-12 px-4">
      <div className="max-w-2xl mx-auto bg-gray-900 rounded-xl shadow-2xl overflow-hidden p-8">
        <div className="text-center mb-8">
          <LuCircleFadingArrowUp className="mx-auto text-5xl mb-4 text-white" />
          <h1 className="text-3xl font-bold text-white">Post a Status</h1>
          <p className="text-gray-400 mt-2">Share what's on your mind</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-8">
          {/* Status Text */}
          <div>
            <label htmlFor="statusText" className="block text-lg font-medium text-white mb-2">
              Status
            </label>
            <textarea
              id="statusText"
              value={statusText}
              onChange={onTextChange}
              placeholder="What's on your mind today?"
              className="w-full px-4 py-3 bg-gray-800 border border-white rounded-lg focus:ring-2 focus:ring-[#FF1493] focus:border-transparent text-white placeholder-gray-400 resize-none"
              rows="4"
              maxLength={500} // Reasonable limit for status text
              aria-label="Enter your status"
            />
            <p className="text-sm text-gray-400 mt-1 text-right">
              {statusText.length}/500
            </p>
          </div>

          {/* Image Upload Section */}
          <div>
            <label className="block text-lg font-medium text-white mb-2">
              Add Images (Optional, up to 4)
            </label>
            <div className="border-2 border-dashed border-white rounded-xl p-4">
              {imagePreviews.length > 0 ? (
                <div className="grid grid-cols-2 gap-4">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="relative">
                      <img
                        src={preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700 transition"
                        aria-label={`Remove image ${index + 1}`}
                      >
                        <FaTrash size={12} />
                      </button>
                    </div>
                  ))}
                  {imagePreviews.length < 4 && (
                    <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-[#FF1493] rounded-lg cursor-pointer hover:bg-gray-800 transition">
                      <FaPlus className="text-white text-2xl" />
                      <input
                        type="file"
                        onChange={onImageChange}
                        accept="image/*"
                        multiple
                        className="hidden"
                        aria-label="Add more images"
                      />
                    </label>
                  )}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <label className="cursor-pointer">
                    <div className="flex flex-col items-center justify-center">
                      <svg
                        className="w-12 h-12 text-[#FF1493] mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      <p className="text-lg text-[#FF1493] font-medium">Click to upload images</p>
                      <p className="text-sm text-gray-400 mt-2">Supports: Image files (up to 4)</p>
                    </div>
                    <input
                      type="file"
                      onChange={onImageChange}
                      accept="image/*"
                      multiple
                      className="hidden"
                      aria-label="Upload images for status"
                    />
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Camera Capture Option */}
          <div className="text-center">
            <label className="inline-flex items-center px-4 py-2 bg-gray-800 border border-white rounded-lg hover:bg-gray-700 transition cursor-pointer">
              <MdCameraAlt className="text-white mr-2" size={20} />
              <span className="text-white font-medium">Capture with Camera</span>
              <input
                type="file"
                onChange={onImageChange}
                accept="image/*"
                capture="environment"
                className="hidden"
                aria-label="Capture image with camera"
              />
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!statusText.trim() && images.length === 0}
            className={`w-full py-3 px-4 rounded-xl font-bold text-lg transition-all duration-300 ${
              (statusText.trim() || images.length > 0)
                ? "bg-gradient-to-r from-[#FF1493] via-[#FF8C00] to-[#FFD700] hover:from-[#FF8C00] hover:to-[#FF1493] shadow-lg hover:shadow-[#FF8C00]/30"
                : "bg-gray-700 text-gray-500 cursor-not-allowed"
            }`}
          >
            Post Status
          </button>
        </form>
      </div>
    </main>
  );
}